/**
 * Utilitaires d'octets et d'encodage. Aucune primitive cryptographique n'est
 * implémentée ici : uniquement des conversions, des assertions et une
 * comparaison à temps constant.
 */
import { base32, base64, base64url, hex } from '@scure/base';
import { InvalidParameterError } from './errors.js';

/** Encode une chaîne en UTF-8 (portable : pas de `Buffer`). */
const TEXT_ENCODER = /* @__PURE__ */ new TextEncoder();
const TEXT_DECODER = /* @__PURE__ */ new TextDecoder('utf-8', { fatal: true });

export function utf8ToBytes(value: string): Uint8Array {
  if (typeof value !== 'string') {
    throw new InvalidParameterError('chaine UTF-8 attendue');
  }
  return TEXT_ENCODER.encode(value);
}

/** Décode de l'UTF-8 strict (lève si la séquence est invalide). */
export function bytesToUtf8(value: Uint8Array): string {
  assertBytes(value, 'valeur');
  try {
    return TEXT_DECODER.decode(value);
  } catch {
    throw new InvalidParameterError('sequence UTF-8 invalide');
  }
}

export function bytesToBase64(value: Uint8Array): string {
  assertBytes(value, 'valeur');
  return base64.encode(value);
}

export function base64ToBytes(value: string): Uint8Array {
  if (typeof value !== 'string') {
    throw new InvalidParameterError('chaine base64 attendue');
  }
  try {
    return base64.decode(value);
  } catch {
    throw new InvalidParameterError('base64 invalide');
  }
}

export function bytesToBase64Url(value: Uint8Array): string {
  assertBytes(value, 'valeur');
  return base64url.encode(value);
}

export function base64UrlToBytes(value: string): Uint8Array {
  if (typeof value !== 'string') {
    throw new InvalidParameterError('chaine base64url attendue');
  }
  try {
    return base64url.decode(value);
  } catch {
    throw new InvalidParameterError('base64url invalide');
  }
}

export function bytesToBase32(value: Uint8Array): string {
  assertBytes(value, 'valeur');
  return base32.encode(value).replace(/=+$/u, '');
}

/**
 * Decode du base32 (RFC 4648) toleré non-canonique : espaces/tirets ignores,
 * casse indifferente, padding `=` optionnel (re-complete avant decodage, seule
 * forme acceptee par `@scure/base`). Utilise pour les graines TOTP saisies ou
 * scannees manuellement.
 */
export function base32ToBytes(value: string): Uint8Array {
  if (typeof value !== 'string') {
    throw new InvalidParameterError('chaine base32 attendue');
  }
  const normalized = value.trim().toUpperCase().replace(/[\s-]+/gu, '').replace(/=+$/u, '');
  if (normalized.length === 0) {
    throw new InvalidParameterError('base32 invalide');
  }
  const remainder = normalized.length % 8;
  const padded = remainder === 0 ? normalized : normalized + '='.repeat(8 - remainder);
  try {
    return base32.decode(padded);
  } catch {
    throw new InvalidParameterError('base32 invalide');
  }
}

export function bytesToHex(value: Uint8Array): string {
  assertBytes(value, 'valeur');
  return hex.encode(value);
}

export function hexToBytes(value: string): Uint8Array {
  if (typeof value !== 'string') {
    throw new InvalidParameterError('chaine hexadecimale attendue');
  }
  try {
    return hex.decode(value.toLowerCase());
  } catch {
    throw new InvalidParameterError('hexadecimal invalide');
  }
}

/** Vérifie que la valeur est bien un `Uint8Array` (et pas un autre TypedArray). */
export function isBytes(value: unknown): value is Uint8Array {
  return value instanceof Uint8Array;
}

export function assertBytes(value: unknown, name: string, length?: number): asserts value is Uint8Array {
  if (!isBytes(value)) {
    throw new InvalidParameterError(`${name}: Uint8Array attendu`);
  }
  if (length !== undefined && value.length !== length) {
    throw new InvalidParameterError(`${name}: ${length} octets attendus, ${value.length} recus`);
  }
}

export function assertNonEmptyString(value: unknown, name: string): asserts value is string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new InvalidParameterError(`${name}: chaine non vide attendue`);
  }
}

export function assertInteger(value: unknown, name: string, min: number, max: number): asserts value is number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) {
    throw new InvalidParameterError(`${name}: entier attendu`);
  }
  if (value < min || value > max) {
    throw new InvalidParameterError(`${name}: doit etre dans [${min}, ${max}], recu ${value}`);
  }
}

/** Concatène des séquences d'octets dans un nouveau tampon. */
export function concatBytes(...parts: readonly Uint8Array[]): Uint8Array {
  let total = 0;
  for (const part of parts) {
    assertBytes(part, 'fragment');
    total += part.length;
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

/**
 * Comparaison à temps constant vis-à-vis du CONTENU (la longueur, elle, est
 * publique : deux longueurs différentes renvoient `false` immédiatement).
 * Indispensable pour comparer tags, hachés et clés sans fuite temporelle.
 */
export function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  assertBytes(a, 'a');
  assertBytes(b, 'b');
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

/**
 * Écrase un tampon. En JavaScript l'effacement mémoire n'est pas garanti (le GC
 * peut avoir recopié le tampon, les `string` sont immuables) : c'est une mesure
 * de réduction de surface, pas une garantie. Voir README §« Limites ».
 */
export function wipe(...buffers: readonly (Uint8Array | null | undefined)[]): void {
  for (const buffer of buffers) {
    if (buffer && isBytes(buffer)) buffer.fill(0);
  }
}

/**
 * Encode `uint32be(longueur) || contenu`. Le préfixe de longueur rend la
 * concaténation de champs NON AMBIGÜE : sans lui, `("ab","c")` et `("a","bc")`
 * produiraient les mêmes données associées.
 */
export function lengthPrefixed(value: Uint8Array): Uint8Array {
  assertBytes(value, 'valeur');
  if (value.length > 0xffffffff) {
    throw new InvalidParameterError('fragment trop long pour un prefixe de 32 bits');
  }
  const out = new Uint8Array(4 + value.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, value.length, false);
  out.set(value, 4);
  return out;
}
